/**
 * CoordinatorQrPanel — the organizer-access link as a scannable QR, for BOTH
 * event kinds. Pass apiBase = `/sports/open-play/${sid}` or `/sports/tournaments/${tid}`.
 * PRIVATE: hand to your crew only; the desk still needs the PIN to unlock.
 */
import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { KeyRound, Eye, EyeOff, Download } from 'lucide-react';
import { toast } from 'sonner';
import { sportsApi } from '../SportsAuthContext';
import { downloadQR } from '../utils/qrDownload';

export default function CoordinatorQrPanel({ apiBase, eventName }) {
  const [access, setAccess] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPin, setShowPin] = useState(false);

  useEffect(() => {
    setAccess(null);
    sportsApi.get(`${apiBase}/organizer-access`)
      .then(r => setAccess(r.data)).catch(() => setAccess({ exists: false }));
  }, [apiBase]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await sportsApi.post(`${apiBase}/organizer-access`, {});
      setAccess({ exists: true, path: r.data.path, pin: r.data.pin });
      toast.success('Coordinator link created');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create coordinator access');
    } finally { setBusy(false); }
  };

  const url = access?.path ? `${window.location.origin}${access.path}` : '';
  const safeName = (eventName || 'event').replace(/\s+/g, '-');
  return (
    <div className="rounded-xl p-5" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}
      data-testid="coordinator-qr-panel">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="h-4 w-4" style={{ color: '#8B5CF6' }} />
        <p className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif', color: '#8B5CF6' }}>
          Coordinator Desk QR — Private, for your crew
        </p>
      </div>
      <p className="text-xs mb-4" style={{ color: '#555' }}>
        Coordinators scan to open this event's desk: collect fees into the event cashbox,
        check participants in, waive with a reason. The desk asks for the PIN and their name —
        every peso is logged per person. Don't post this one publicly.
      </p>
      {!access ? (
        <p className="text-sm text-center py-6" style={{ color: '#444' }}>Loading…</p>
      ) : !access.exists ? (
        <div className="flex flex-col items-center py-4">
          <p className="text-sm mb-3" style={{ color: '#737373' }}>No coordinator link yet for this event.</p>
          <button onClick={generate} disabled={busy} data-testid="coordinator-qr-generate"
            className="px-4 py-2 rounded-lg text-xs font-bold uppercase"
            style={{ background: '#8B5CF618', color: '#8B5CF6', border: '1px solid #8B5CF640' }}>
            {busy ? 'Working…' : 'Generate coordinator link + PIN'}
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center">
          <div className="p-4 rounded-xl mb-3" style={{ background: '#fff' }}>
            <QRCodeSVG id="qr-coordinator" value={url} size={160} level="H" includeMargin
              data-label={eventName} data-sublabel="Coordinator desk — PIN required" />
          </div>
          <p className="text-xs font-bold mb-3" style={{ color: '#A3A3A3' }}>Coordinator desk — PIN required</p>
          <button data-testid="coordinator-qr-download"
            onClick={() => downloadQR('qr-coordinator', `coordinator-desk-${safeName}.png`)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold"
            style={{ background: '#8B5CF618', color: '#8B5CF6', border: '1px solid #8B5CF630' }}>
            <Download className="h-3.5 w-3.5" /> Download Poster QR
          </button>
          <div className="flex items-center gap-2 mt-3">
            <span className="text-xs px-2.5 py-1.5 rounded-lg font-bold"
              style={{ background: '#0A0A0A', border: '1px solid #1E1E1E', color: '#FFB300', letterSpacing: 2 }}>
              PIN: {showPin ? access.pin : '••••••'}
            </span>
            <button onClick={() => setShowPin(v => !v)} data-testid="coordinator-qr-pin-toggle"
              className="px-2 py-1.5 rounded-lg" style={{ background: '#1E1E1E', color: '#737373' }}>
              {showPin ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
          </div>
          <p className="text-[11px] mt-2 text-center" style={{ color: '#525252' }}>
            Share the PIN separately (rotate it anytime from the Dashboard tab).
          </p>
        </div>
      )}
    </div>
  );
}
